import { type ReactNode, useEffect, useRef } from "react";
import { cx } from "class-variance-authority";
import { useIntersectionObserver } from "usehooks-ts";

import { Button } from "../Button/Button";
import { PageLoader } from "../Loader/PageLoader";
import * as styles from "./LoadMore.module.scss";

type LoadMoreProps = {
  next: () => void;
  hasMore: boolean;
  isFetching: boolean;
  offset?: number;
  root?: Element | Document | null;
  loader?: ReactNode;
  mode?: "auto" | "button";
  className?: string;
};

const LoadMore = ({
  next,
  hasMore,
  isFetching,
  offset = 300,
  root,
  loader,
  mode = "auto",
  className,
}: LoadMoreProps) => {
  const { ref, isIntersecting } = useIntersectionObserver({
    root,
    rootMargin: `${offset}px 0px`,
  });

  const nextRef = useRef(next);

  useEffect(() => {
    nextRef.current = next;
  }, [next]);

  // Asking only when the sentinel crosses into view strands the list: parking
  // at the bottom leaves it permanently intersecting, so no further crossing
  // ever fires. Re-checking whenever the fetch settles keeps pages coming until
  // the sentinel is pushed out of range or there is nothing left to load.
  useEffect(() => {
    if (mode !== "auto" || !isIntersecting || !hasMore || isFetching) return;

    nextRef.current();
  }, [mode, isIntersecting, hasMore, isFetching]);

  if (mode === "button") {
    if (!hasMore) return null;

    return (
      <div className={cx(styles.button, className)}>
        <Button size="md" loading={isFetching} onClick={next}>
          Load More
        </Button>
      </div>
    );
  }

  return (
    <>
      <div ref={ref} data-testid="load-more" className={styles.loadMore} />
      {isFetching && hasMore
        ? (loader ?? <PageLoader className={styles.loader} />)
        : null}
    </>
  );
};

export { LoadMore, type LoadMoreProps };
